// src/components/ConditionalLogicConfig.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    IconButton,
    Paper,
    Divider,
    Chip,
    alpha,
    Alert,
    FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { ConditionalOperators, ConditionalActions } from '../types/schema';

const ConditionalLogicConfig = ({
    type, // 'field' or 'section'
    conditions,
    onChange,
    availableFields = [], // Array of {sectionId, sectionTitle, fieldName, fieldLabel, fieldType}
    currentSectionId,
    allSections = [] // For section skip logic
}) => {
    const [localConditions, setLocalConditions] = useState(conditions || []);

    useEffect(() => {
        setLocalConditions(conditions || []);
    }, [conditions]);

    const handleAddCondition = () => {
        const newCondition = type === 'field'
            ? {
                id: `condition_${Date.now()}`,
                sourceSectionId: currentSectionId, // Default to current section for field conditions
                sourceFieldName: '',
                operator: ConditionalOperators.EQUALS,
                triggerValue: '',
                action: ConditionalActions.HIDE,
                valueType: 'string'
            }
            : {
                id: `skiprule_${Date.now()}`,
                sourceSectionId: '',
                sourceFieldName: '',
                operator: ConditionalOperators.EQUALS,
                triggerValue: '',
                action: ConditionalActions.SKIP_SECTION,
                skipEntireSection: true
            };

        const updated = [...localConditions, newCondition];
        setLocalConditions(updated);
        onChange(updated);
    };

    const handleDeleteCondition = (index) => {
        const updated = localConditions.filter((_, i) => i !== index);
        setLocalConditions(updated);
        onChange(updated);
    };

    const handleConditionChange = (index, field, value) => {
        const updated = [...localConditions];
        updated[index] = { ...updated[index], [field]: value };
        setLocalConditions(updated);
        onChange(updated);
    };

    // Get fields from a specific section
    const getFieldsFromSection = (sectionId) => {
        if (type === 'field') {
            // For field conditions, only show fields from current section
            return availableFields.filter(field => field.sectionId === sectionId);
        } else {
            // For section skip rules, show fields from previous sections only
            const currentSectionIndex = allSections.findIndex(s => s.id === currentSectionId);
            const previousSections = allSections.slice(0, currentSectionIndex);
            const previousSectionIds = previousSections.map(s => s.id);

            return availableFields.filter(field =>
                previousSectionIds.includes(field.sectionId)
            );
        }
    };

    // Get available source sections (for section skip rules)
    const getAvailableSourceSections = () => {
        if (type === 'field') {
            // For field conditions, only current section
            return allSections.filter(s => s.id === currentSectionId);
        } else {
            // For section skip rules, all previous sections
            const currentSectionIndex = allSections.findIndex(s => s.id === currentSectionId);
            return allSections.slice(0, currentSectionIndex);
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {type === 'field' ? 'Field Visibility Rules' : 'Section Skip Rules'}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddCondition}
                    variant="outlined"
                    size="small"
                >
                    Add Rule
                </Button>
            </Box>

            {type === 'field' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Configure rules to hide this field based on values in other fields <strong>within the same section</strong>.
                </Alert>
            )}

            {type === 'section' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Configure rules to skip this entire section based on values in <strong>previous sections</strong>.
                </Alert>
            )}

            {localConditions.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        bgcolor: alpha('#1976d2', 0.04)
                    }}
                >
                    <Typography color="text.secondary">
                        {type === 'field'
                            ? 'No visibility rules configured. This field will always be visible.'
                            : 'No skip rules configured. This section will always be shown.'
                        }
                    </Typography>
                </Paper>
            ) : (
                localConditions.map((condition, index) => (
                    <Paper
                        key={condition.id}
                        elevation={2}
                        sx={{
                            p: 2,
                            mb: 2,
                            borderLeft: 4,
                            borderColor: type === 'field' ? 'primary.main' : 'secondary.main',
                            bgcolor: alpha(type === 'field' ? '#1976d2' : '#9c27b0', 0.05)
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                {type === 'field' ? (
                                    <VisibilityOffIcon color="primary" />
                                ) : (
                                    <SkipNextIcon color="secondary" />
                                )}
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Rule {index + 1}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteCondition(index)}
                                color="error"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                            {/* Source Section Selector (only for section skip rules) */}
                            {type === 'section' && (
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Source Section</InputLabel>
                                        <Select
                                            value={condition.sourceSectionId || ''}
                                            onChange={(e) => handleConditionChange(index, 'sourceSectionId', e.target.value)}
                                            label="Source Section"
                                        >
                                            <MenuItem value="">Select a section</MenuItem>
                                            {getAvailableSourceSections().map(section => (
                                                <MenuItem key={section.id} value={section.id}>
                                                    {section.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            Previous section to check for values
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Source Field Selector */}
                            <Grid item xs={12} md={type === 'section' ? 6 : 12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Source Field</InputLabel>
                                    <Select
                                        value={condition.sourceFieldName || ''}
                                        onChange={(e) => handleConditionChange(index, 'sourceFieldName', e.target.value)}
                                        label="Source Field"
                                        disabled={type === 'section' && !condition.sourceSectionId}
                                    >
                                        <MenuItem value="">Select a field</MenuItem>
                                        {getFieldsFromSection(condition.sourceSectionId || currentSectionId).map(field => (
                                            <MenuItem key={field.fieldName} value={field.fieldName}>
                                                {field.fieldLabel} ({field.fieldType})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        Field whose value will trigger the rule
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            {/* Operator */}
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        value={condition.operator || ConditionalOperators.EQUALS}
                                        onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                                        label="Operator"
                                    >
                                        <MenuItem value={ConditionalOperators.EQUALS}>Equals</MenuItem>
                                        <MenuItem value={ConditionalOperators.NOT_EQUALS}>Does Not Equal</MenuItem>
                                        <MenuItem value={ConditionalOperators.CONTAINS}>Contains</MenuItem>
                                        <MenuItem value={ConditionalOperators.NOT_CONTAINS}>Does Not Contain</MenuItem>
                                        <MenuItem value={ConditionalOperators.IS_EMPTY}>Is Empty</MenuItem>
                                        <MenuItem value={ConditionalOperators.IS_NOT_EMPTY}>Is Not Empty</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Trigger Value (not shown for IS_EMPTY/IS_NOT_EMPTY) */}
                            {condition.operator !== ConditionalOperators.IS_EMPTY &&
                                condition.operator !== ConditionalOperators.IS_NOT_EMPTY && (
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Trigger Value"
                                            value={condition.triggerValue || ''}
                                            onChange={(e) => handleConditionChange(index, 'triggerValue', e.target.value)}
                                            placeholder="Value that triggers the rule"
                                        />
                                    </Grid>
                                )}

                            {/* Action */}
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Action</InputLabel>
                                    <Select
                                        value={condition.action || ConditionalActions.HIDE}
                                        onChange={(e) => handleConditionChange(index, 'action', e.target.value)}
                                        label="Action"
                                        disabled={type === 'section'} // Always SKIP_SECTION for sections
                                    >
                                        {type === 'field' ? (
                                            [
                                                <MenuItem key="hide" value={ConditionalActions.HIDE}>Hide This Field</MenuItem>,
                                                <MenuItem key="show" value={ConditionalActions.SHOW}>Show This Field</MenuItem>,
                                                // <MenuItem key="disable" value={ConditionalActions.DISABLE}>Disable This Field</MenuItem>,
                                                // <MenuItem key="enable" value={ConditionalActions.ENABLE}>Enable This Field</MenuItem>
                                            ]
                                        ) : (
                                            <MenuItem value={ConditionalActions.SKIP_SECTION}>
                                                Skip This Entire Section
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Rule Summary */}
                        <Box mt={2} pt={1} borderTop={1} borderColor="divider">
                            <Typography variant="caption" color="text.secondary">
                                <strong>Rule Summary:</strong>{' '}
                                {type === 'field' ? 'This field will be ' : 'This section will be '}
                                <Chip
                                    label={condition.action.replace('_', ' ').toLowerCase()}
                                    size="small"
                                    color={condition.action === ConditionalActions.HIDE ? 'error' : 'primary'}
                                    variant="outlined"
                                    sx={{ mx: 0.5 }}
                                />
                                {condition.sourceFieldName && ` when "${condition.sourceFieldName}" `}
                                {condition.operator && ` ${condition.operator.replace('_', ' ').toLowerCase()} `}
                                {condition.triggerValue && ` "${condition.triggerValue}"`}
                                {condition.operator === ConditionalOperators.IS_EMPTY && ' is empty'}
                                {condition.operator === ConditionalOperators.IS_NOT_EMPTY && ' is not empty'}
                            </Typography>
                        </Box>
                    </Paper>
                ))
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
                <strong>Note:</strong> {type === 'field'
                    ? 'Field conditions only work within the same section. Multiple rules are evaluated with AND logic.'
                    : 'Section skip rules only check values from previous sections. If any rule is true, the section is skipped.'
                }
            </Typography>
        </Box>
    );
};

export default ConditionalLogicConfig;